import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Banknote, FileText, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader, StatCard, StatusBadge, EmptyState } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/components/staff/Panel";
import { api, fmtDate, pretty, tzs } from "@/lib/api";
import InvoiceForm from "@/components/billing/InvoiceForm";
import InvoiceDetail from "@/components/billing/InvoiceDetail";
import { TaxRates, Plans, Subscriptions, BillingSettings } from "@/components/billing/BillingAdmin";
import { Accounts, MonthlySummaries } from "@/components/billing/Commercial";

const FILTERS = ["all", "draft", "issued", "partially_paid", "overdue", "paid", "void"];

function Summary({ refreshKey }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/billing/summary").then((r) => setS(r.data)); }, [refreshKey]);
  if (!s) return null;
  return (
    <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
      <StatCard label="Outstanding" value={tzs(s.outstanding)} icon={Wallet} testId="bill-stat-outstanding" />
      <StatCard label={`Overdue (${s.overdue_count})`} value={tzs(s.overdue_amount)} icon={AlertTriangle} tone="danger" testId="bill-stat-overdue" />
      <StatCard label="Collected this month" value={tzs(s.collected_this_month)} icon={Banknote} tone="ok" testId="bill-stat-collected" />
      <StatCard label={s.draft_count != null ? "Drafts / active subs" : "Open invoices"} value={s.draft_count != null ? `${s.draft_count} / ${s.active_subscriptions}` : s.open_count} icon={FileText} tone="warn" testId="bill-stat-open" />
    </div>
  );
}

function InvoiceList({ refreshKey, onOpen, staff }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get("/billing/invoices").then((r) => setItems(r.data)); }, [refreshKey]);
  const shown = items.filter((i) => filter === "all" || i.display_status === filter);
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">{FILTERS.filter((k) => staff || k !== "draft").map((k) => (
        <button key={k} onClick={() => setFilter(k)} data-testid={`inv-filter-${k}`} className={`rounded-full border px-3 py-1 text-xs font-medium ${filter === k ? "border-primary bg-primary text-black" : "bg-card hover:bg-muted"}`}>
          {pretty(k)} ({items.filter((i) => k === "all" || i.display_status === k).length})</button>
      ))}</div>
      {!shown.length && <EmptyState title="No invoices" testId="invoices-empty" />}
      {shown.map((i) => (
        <button key={i.id} onClick={() => onOpen(i.id)} className="flex w-full flex-col gap-2 rounded-lg border bg-card p-4 text-left transition-colors hover:border-primary/40 sm:flex-row sm:items-center sm:justify-between" data-testid={`invoice-row-${i.id}`}>
          <div><div className="font-semibold"><span className="font-mono">{i.number || "DRAFT"}</span> · {i.company_name}</div>
            <div className="text-xs text-muted-foreground">{i.issue_date ? `Issued ${fmtDate(i.issue_date)} · ` : ""}Due {fmtDate(i.due_date)} · Total {tzs(i.total)}</div></div>
          <div className="flex items-center gap-3"><span className="text-sm font-semibold">Balance {tzs(i.balance)}</span><StatusBadge kind="billing" status={i.display_status} testId={`invoice-status-${i.id}`} /></div>
        </button>
      ))}
    </div>
  );
}

function Receipts({ refreshKey }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/billing/payments").then((r) => setItems(r.data)); }, [refreshKey]);
  if (!items.length) return <EmptyState title="No payments recorded yet" testId="receipts-empty" />;
  return (
    <div className="space-y-2">{items.map((p) => (
      <div key={p.id} className="flex flex-col gap-1 rounded-lg border bg-card p-4 sm:flex-row sm:items-center sm:justify-between" data-testid={`receipt-row-${p.id}`}>
        <div><span className="font-mono font-semibold">{p.receipt_number}</span> · {tzs(p.amount)} · {p.invoice_number} · {p.company_name}<div className="text-xs text-muted-foreground">{pretty(p.channel)} · ref {p.reference} · {fmtDate(p.paid_on)}</div></div>
        <div className="flex items-center gap-2"><StatusBadge kind="billing" status={p.status} /><a className="text-sm text-primary hover:underline" href={`/billing/receipt/${p.id}`} target="_blank" rel="noreferrer" data-testid={`receipt-open-${p.id}`}>View receipt</a></div>
      </div>
    ))}</div>
  );
}

export default function Billing() {
  const { user, isStaff } = useAuth();
  const [params, setParams] = useSearchParams();
  const [key, setKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const openId = params.get("invoice");
  const open = (id) => setParams(id ? { invoice: id } : {});
  const bump = () => setKey((k) => k + 1);
  return (
    <div>
      <PageHeader title={isStaff ? "Billing administration" : "Billing"} subtitle={isStaff ? "Plans, subscriptions, invoices, manual payments and receipts (TZS). No online payments are processed." : "Your company's invoices, payments and receipts. Payments are made offline and recorded by V.A."}>
        {can(user, "invoices_create") && <Button onClick={() => setCreating(true)} data-testid="invoice-new-btn">New invoice</Button>}
      </PageHeader>
      <Summary refreshKey={key} />
      <Tabs defaultValue="invoices">
        <TabsList className="mb-4 h-auto flex-wrap justify-start">
          <TabsTrigger value="invoices" data-testid="bill-tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="receipts" data-testid="bill-tab-receipts">Payments &amp; receipts</TabsTrigger>
          <TabsTrigger value="subs" data-testid="bill-tab-subs">Subscriptions</TabsTrigger>
          <TabsTrigger value="summaries" data-testid="bill-tab-summaries">Monthly summaries</TabsTrigger>
          {can(user, "accounts_manage") && <TabsTrigger value="accounts" data-testid="bill-tab-accounts">Accounts &amp; trials</TabsTrigger>}
          {isStaff && <TabsTrigger value="plans" data-testid="bill-tab-plans">Price list</TabsTrigger>}
          {isStaff && <TabsTrigger value="tax" data-testid="bill-tab-tax">Tax rates</TabsTrigger>}
          {isStaff && <TabsTrigger value="settings" data-testid="bill-tab-settings">Settings</TabsTrigger>}
        </TabsList>
        <TabsContent value="invoices"><InvoiceList refreshKey={key} onOpen={open} staff={isStaff} /></TabsContent>
        <TabsContent value="receipts"><Receipts refreshKey={key} /></TabsContent>
        <TabsContent value="subs"><Subscriptions onDraft={(id) => { bump(); open(id); }} /></TabsContent>
        <TabsContent value="summaries"><MonthlySummaries /></TabsContent>
        {can(user, "accounts_manage") && <TabsContent value="accounts"><Accounts /></TabsContent>}
        {isStaff && <TabsContent value="plans"><Plans /></TabsContent>}
        {isStaff && <TabsContent value="tax"><TaxRates /></TabsContent>}
        {isStaff && <TabsContent value="settings"><BillingSettings /></TabsContent>}
      </Tabs>
      {creating && <InvoiceForm onClose={() => setCreating(false)} onSaved={(inv) => { bump(); open(inv.id); }} />}
      {openId && <InvoiceDetail id={openId} onClose={() => open(null)} onChanged={bump} />}
    </div>
  );
}
